// @lumenlan/account-sdk —— vendored copy（keytail-web）
//
// 源：lumenlan-platform/sdks/web/account-sdk/src/index.ts
// commit: 22325c16dcc55b229d0f304a25df61a7cee77b4f（2026-05-29, plan 22 M1 轻 BFF 跨子域 SSO）
//
// 与 beemail-web 的 vendored copy 同源（逐字一致 + 本仓仅增 currentAccessToken() 桥接 getter）。
// keytail 与 lumenlan-platform 不在同一 monorepo，无法走 admin-console 那套 vite alias 直接消费
// platform 源码的方式。改 vendored copy：改动须在 lumenlan-platform 上游先合，本仓再 rsync
// 这份文件并更新顶部 commit hash。
//
// 设计参考：lumenlan-platform/docs/ecosystem/10-platform-sdks.md；plan 22 M1（轻 BFF）。
//
// 形态（对齐 IETF OAuth 2.0 for Browser-Based Apps BCP）：
//   - access token **仅存内存**（页面刷新即丢，绝不落 localStorage）——XSS 拿不到可长期复用的凭据。
//   - **不再持久化 refresh token**。长期凭据的唯一载体是 account 域种的 host-only 会话 cookie
//     `__lumenlan_sess`（HttpOnly，浏览器读不到）。刷新 / 首屏静默登录都走
//     POST /v1/auth/session/token：凭 cookie 换一枚新的短时 access token（credentials:'include'）。
//   - PKCE 仅用于「首次登录无会话」「桌面端」「跨站第三方」；生态内 first-party 子域产品
//     bootstrap() 直接 session/token 零 redirect 静默进入（见 plan 22 M3）。
//
// keytail 接入要点（P8）：SSO 只替换「身份登录」一层（怎么拿 account access token）；拿到后的
// vault PIN 解锁 / 字段加解密 / 同步 / 改 PIN 链路完全不变（见 plan 08 §范围红线）。
// keytail-server 的 Bearer 调用复用同一枚 access token——经 currentAccessToken() 取（SDK 内部
// scheduleRefresh 已主动保鲜）。跨子域生效前提：keytailapp 须在 account oauth-clients 注册表里
// （有 allowed_redirect_uris 的 web client），其 origin 才被 session/token 端点的 Origin 白名单放行。

export interface AccountSDKOptions {
  baseURL: string;          // 形如 https://account.lumenlan.com
  product: string;          // lumi | deerlight | beemail | keytail | robin | platform
  // admin-console 等平台前端 product='platform' 但 OAuth client_id 不同（lumenlan-admin-console）。
  // 不传时回落到 product。
  clientID?: string;
  deviceId?: string;        // 持久化在 localStorage（device id 不是 secret）
  onUnauthorized?: () => void; // 会话失效时触发，应用层导航到登录页
}

export interface TokenSet {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string; // BFF web 模式忽略；保留字段仅为兼容旧响应解析
  refreshExpiresIn?: number;
}

export interface User {
  id: string;
  nickname: string;
  avatarUrl: string;
  phone?: string;
  role: string;
  adminLevel: string;
  points?: number;
  memberships?: Array<{ product: string; tier: string; expireAt: string | null }>;
}

export interface PublicAuthConfig {
  captchaEnabled: boolean;
  captchaPrefix?: string;
  captchaSceneId?: string;
}

type Listener = (u: User | null) => void;

export class AccountSDK {
  private opts: AccountSDKOptions;
  private accessToken: string | null = null;
  private expiresAt = 0;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<Listener>();
  private user: User | null = null;

  constructor(opts: AccountSDKOptions) {
    this.opts = opts;
  }

  // bootstrap 在 app mount 前调用：凭 host-only 会话 cookie 静默换 access + 拉 user。
  // 有会话 → true（零 redirect 进入）；无会话 / 失效 → false，由调用方决定跳登录页。
  async bootstrap(): Promise<boolean> {
    try {
      await this.silentRenew();
      const user = await this.fetchCurrentUser();
      this.setUser(user);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  // ---- 公共 API ----

  async loginPhone(phone: string, smsCode: string): Promise<User> {
    // 后端 LoginResp 形如 {token, user}；登录成功后端会种 host-only 会话 cookie（plan 22 M1）。
    // SDK 只取 access token 进内存，忽略响应里的 refreshToken（不再持久化）。
    const data = await this.post<{ token?: string; accessToken?: string; expiresIn?: number; user: User }>(
      "/v1/auth/login/phone",
      { phone, smsCode, deviceInfo: { platform: "web", appVersion: "1.0.0" } },
      { credentials: "include" }, // 关键：让 Set-Cookie 会话 cookie 落地
    );
    this.applyAccess(normalizeTokenSet(data));
    this.setUser(data.user);
    return data.user;
  }

  async sendSMSCode(
    phone: string,
    options?: {
      scene?: "login" | "bind";
      captcha?: { captchaVerifyParam: string };
    },
  ): Promise<{ devCode: string }> {
    const payload: Record<string, string> = { phone };
    if (options?.scene) payload.scene = options.scene;
    if (options?.captcha?.captchaVerifyParam) {
      payload.captchaVerifyParam = options.captcha.captchaVerifyParam;
    }
    const data = await this.post<{ devCode?: string }>("/v1/auth/sms/send", payload);
    return { devCode: data?.devCode ?? "" };
  }

  async getPublicAuthConfig(): Promise<PublicAuthConfig> {
    const resp = await fetch(this.opts.baseURL + "/v1/auth/captcha/config", { credentials: "include" });
    return parseEnvelope<PublicAuthConfig>(resp);
  }

  // 用一次性短码换登录（微信扫码回跳）。后端在 exchange 成功时种会话 cookie（plan 22 M1）。
  async exchangeShortToken(shortToken: string): Promise<User> {
    const data = await this.post<{ token?: string; accessToken?: string; expiresIn?: number; user: User }>(
      "/v1/auth/exchange-short-token",
      { shortToken },
      { credentials: "include" },
    );
    this.applyAccess(normalizeTokenSet(data));
    this.setUser(data.user);
    return data.user;
  }

  // 扫码登录后凭 binding_token 补绑手机号完成登录。后端成功时种会话 cookie。
  async completeWechatBindPhone(bindingToken: string, phone: string, smsCode: string): Promise<User> {
    const data = await this.post<{ token?: string; accessToken?: string; expiresIn?: number; user: User }>(
      "/v1/auth/bind/phone",
      { bindingToken, phone, smsCode },
      { credentials: "include" },
    );
    this.applyAccess(normalizeTokenSet(data));
    this.setUser(data.user);
    return data.user;
  }

  async updateProfile(req: { nickname?: string; avatarUrl?: string }): Promise<User> {
    const resp = await this.fetch(this.opts.baseURL + "/v1/user/profile", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    });
    const user = await parseEnvelope<User>(resp);
    this.setUser(user);
    return user;
  }

  // PKCE Authorization Code Flow（首次登录无会话 / 桌面端 / 跨站第三方）。
  async startPKCE(scopes: string[] = ["identity"]): Promise<{ url: string; state: string }> {
    const codeVerifier = randomString(64);
    const codeChallenge = await sha256Base64URL(codeVerifier);
    const state = randomString(16);
    sessionStorage.setItem("lumenlan.pkce.verifier", codeVerifier);
    sessionStorage.setItem("lumenlan.pkce.state", state);
    const u = new URL(this.opts.baseURL + "/v1/auth/authorize");
    u.searchParams.set("response_type", "code");
    u.searchParams.set("client_id", this.opts.clientID ?? this.opts.product);
    u.searchParams.set("redirect_uri", window.location.origin + "/callback");
    u.searchParams.set("scope", scopes.join(" "));
    u.searchParams.set("state", state);
    u.searchParams.set("code_challenge", codeChallenge);
    u.searchParams.set("code_challenge_method", "S256");
    return { url: u.toString(), state };
  }

  async exchangePKCE(code: string, state: string): Promise<User> {
    if (state !== sessionStorage.getItem("lumenlan.pkce.state")) {
      throw new Error("pkce: state mismatch");
    }
    const verifier = sessionStorage.getItem("lumenlan.pkce.verifier") ?? "";
    const tokens = await this.post<{ token?: string; accessToken?: string; expiresIn?: number }>(
      "/v1/auth/token/exchange",
      {
        grant_type: "authorization_code",
        code,
        redirect_uri: window.location.origin + "/callback",
        client_id: this.opts.clientID ?? this.opts.product,
        code_verifier: verifier,
      },
      { credentials: "include" },
    );
    sessionStorage.removeItem("lumenlan.pkce.verifier");
    sessionStorage.removeItem("lumenlan.pkce.state");
    this.applyAccess(normalizeTokenSet(tokens));
    const user = await this.fetchCurrentUser();
    this.setUser(user);
    return user;
  }

  private async fetchCurrentUser(): Promise<User> {
    const resp = await this.fetch(this.opts.baseURL + "/v1/me");
    return parseEnvelope<User>(resp);
  }

  // logout：吊销 host-only 会话（CSRF token 双提交）+ 清内存 access。
  async logout(): Promise<void> {
    try {
      const csrf = await this.fetchCSRF();
      await fetch(this.opts.baseURL + "/v1/auth/session/logout", {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "X-Lumenlan-Product": this.opts.product },
        credentials: "include",
      });
    } catch {
      // 会话可能已失效；忽略，照常清本地。
    }
    this.clearTokens();
    this.setUser(null);
  }

  // 给 fetch 包装：自动带 Authorization + 401 时静默续会话再重试一次。
  async fetch(input: string | URL, init: RequestInit = {}): Promise<Response> {
    await this.ensureFresh();
    const headers = new Headers(init.headers);
    if (this.accessToken) headers.set("Authorization", "Bearer " + this.accessToken);
    headers.set("X-Lumenlan-Product", this.opts.product);
    if (this.opts.deviceId) headers.set("X-Lumenlan-Device-Id", this.opts.deviceId);
    const resp = await fetch(input, { ...init, headers, credentials: init.credentials ?? "include" });
    if (resp.status === 401) {
      try {
        await this.silentRenew();
        headers.set("Authorization", "Bearer " + this.accessToken);
        return fetch(input, { ...init, headers, credentials: init.credentials ?? "include" });
      } catch {
        this.clearTokens();
        this.setUser(null);
        this.opts.onUnauthorized?.();
      }
    }
    return resp;
  }

  onAuthChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ---- 内部 ----

  // silentRenew：凭 host-only 会话 cookie 换一枚新的短时 access token（不下发 RT）。
  // 替代旧版的 token/refresh（RT）。会话不存在 / 失效 → 抛错，调用方走未登录分支。
  private async silentRenew(): Promise<void> {
    const resp = await fetch(this.opts.baseURL + "/v1/auth/session/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Lumenlan-Product": this.opts.product,
      },
      body: JSON.stringify({ clientId: this.opts.clientID ?? this.opts.product }),
      credentials: "include",
    });
    const data = await parseEnvelope<{ accessToken: string; expiresIn: number }>(resp);
    if (!data?.accessToken) throw new Error("session/token 未返回 accessToken");
    this.applyAccess({ accessToken: data.accessToken, expiresIn: data.expiresIn });
  }

  private async fetchCSRF(): Promise<string> {
    const resp = await fetch(this.opts.baseURL + "/v1/auth/session/csrf", { credentials: "include" });
    const data = await parseEnvelope<{ csrfToken: string }>(resp);
    return data?.csrfToken ?? "";
  }

  private applyAccess(t: TokenSet) {
    this.accessToken = t.accessToken;
    this.expiresAt = Date.now() + (t.expiresIn - 60) * 1000; // 提前 60s 视为过期
    this.scheduleRefresh();
  }

  private clearTokens() {
    this.accessToken = null;
    this.expiresAt = 0;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  }

  private scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const delay = Math.max(0, this.expiresAt - Date.now());
    this.refreshTimer = setTimeout(() => {
      this.silentRenew().catch(() => {
        this.clearTokens();
        this.setUser(null);
        this.opts.onUnauthorized?.();
      });
    }, delay);
  }

  private async ensureFresh(): Promise<void> {
    if (!this.accessToken) return;
    if (Date.now() >= this.expiresAt) {
      await this.silentRenew();
    }
  }

  private async post<T>(path: string, body: any, extra: RequestInit = {}): Promise<T> {
    const resp = await fetch(this.opts.baseURL + path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Lumenlan-Product": this.opts.product,
        ...(this.opts.deviceId ? { "X-Lumenlan-Device-Id": this.opts.deviceId } : {}),
      },
      body: JSON.stringify(body),
      ...extra,
    });
    return parseEnvelope<T>(resp);
  }

  private setUser(u: User | null) {
    this.user = u;
    this.listeners.forEach(fn => fn(u));
  }

  currentUser(): User | null { return this.user; }

  // currentAccessToken（本仓增）：同步取当前内存 access token，供 keytail-server 的 Bearer 调用桥接
  // （见 api.ts setTokenProvider）。SDK 内部 scheduleRefresh 提前 60s 主动续，故同步读到的多为新鲜值；
  // 真过期场景由 keytail api 的 401 透传 + onUnauthorized 跳登录兜底。
  currentAccessToken(): string | null { return this.accessToken; }
}

// ---- token helpers ----

// normalizeTokenSet 兼容后端不同的 token 字段命名：
//   - lumi/account 历史协议：`{token, user}`（无 expiresIn）→ 当作 accessToken，TTL 默认 15min
//   - OAuth 风格：`{accessToken, expiresIn}` → 透传
// BFF web 模式忽略 refreshToken（不再持久化）。
function normalizeTokenSet(raw: { token?: string; accessToken?: string; expiresIn?: number }): TokenSet {
  const accessToken = raw.accessToken || raw.token
  if (!accessToken) {
    throw new Error("登录响应缺少 token 字段")
  }
  return {
    accessToken,
    expiresIn: raw.expiresIn && raw.expiresIn > 0 ? raw.expiresIn : 15 * 60,
  }
}

// ---- error helpers ----

async function parseEnvelope<T>(resp: Response): Promise<T> {
  const raw = await resp.text()
  let env: any = null
  if (raw) {
    try { env = JSON.parse(raw) } catch { /* 非 JSON 走原文兜底 */ }
  }
  if (!resp.ok) {
    const msg = (env && (env.msg || env.error || env.message)) || raw || `请求失败 (${resp.status})`
    throw new Error(msg)
  }
  if (env && typeof env === "object" && "code" in env && env.code !== 0) {
    throw new Error(env.msg || env.error || `操作失败 (code=${env.code})`)
  }
  return ((env && "data" in env) ? env.data : env) as T
}

// ---- crypto helpers (PKCE) ----

function randomString(len: number): string {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, "0")).join("").slice(0, len);
}

async function sha256Base64URL(s: string): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(s));
  return base64UrlEncode(buf);
}

function base64UrlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
